import type { JSX, PointerEvent } from 'react';
import { useMemo, useState } from 'react';
import { ZONAS_SBTN, tRatio, type Resultado, type SugestaoDeCamadas } from '../../cptu/motor';
import {
  ARRASTO_DO_ZOOM, BarraDoZoom, ControleDaEscala, dominioDaEscala, dominioDe, escala, limitesFixos, marcas, marcasDaEscala,
  numeroBr, useArrastoVertical, useLargura, zoomRecortado,
  type EscalaDoGrafico, type TrechoDoZoom,
} from '../../cptu/visual';

/**
 * As trilhas da janela «Camadas automáticas» (onda 347, Tarefa 10; spec 2026-10-22 §6, R11): quatro trilhas lado a lado,
 * no mesmo eixo de profundidade —
 *  · a **estatística** do método: no T-ratio, o T de cada leitura (`tRatio`, a mesma conta do motor) com o limite tracejado
 *    e os picos que viraram fronteira marcados; no agrupamento contíguo, o custo W(k) pelo número de grupos, com o escolhido
 *    marcado (essa não é por profundidade: é um gráfico à parte, no lugar da trilha);
 *  · a **grandeza** da sugestão (o Ic, ou o qt quando o T-ratio é sobre log₁₀ qt e no agrupamento) com a média de cada
 *    camada em degraus (no qt, a média de log₁₀ qt, escrita em MPa);
 *  · a **zona SBTn** medida, leitura a leitura, com a cor do token da zona;
 *  · as **camadas** sugeridas, com o nome (C1…Cn, ou a unidade).
 * As fronteiras sugeridas atravessam as quatro, tracejadas.
 *
 * O zoom de profundidade é o da Tarefa 4 (R13, `zoomDeProfundidade.tsx`): arrastar para baixo dá o trecho, para cima volta
 * à vertical inteira, o toque longo começa o gesto no celular, e a barra «Profundidade de [ ] a [ ] m». A escala
 * horizontal (R12, Tarefa 3) vale na trilha da grandeza e na do T, pelo botão «Escala» (a chave da grandeza: `ic`, `qt`
 * ou `tratio`), guardada como as outras.
 */

/** As medidas do desenho, em pixel (as das trilhas da vertical, `Trilhas.tsx`). */
const EIXO = 44;
const CABECALHO = 40;
const RODAPE = 8;
const ALTURA = 360;
const LARGURA_MINIMA = 64;
/** A faixa do agrupamento: a altura do gráfico do custo, no lugar da trilha da estatística. */
const MARGEM_DO_CUSTO = 24;

/** O log₁₀ de um número positivo; NaN no resto. */
const log10 = (x: number): number => (Number.isFinite(x) && x > 0 ? Math.log10(x) : NaN);

/** A trilha da grandeza: o que se desenha e a chave da escala. */
export function grandezaDaTrilha(s: SugestaoDeCamadas): { chave: 'ic' | 'qt'; titulo: string; unidade: string } {
  const sobreQt = s.metodo === 'agrupamento' || s.parametros.grandezaLogQt === 1;
  return sobreQt ? { chave: 'qt', titulo: 'qt', unidade: 'MPa' } : { chave: 'ic', titulo: 'Ic', unidade: '' };
}

/** As leituras do trecho [a, b] (as outras viram NaN, para o automático sair do trecho visível). */
function noTrecho(z: ArrayLike<number>, v: ArrayLike<number>, a: number, b: number): Float64Array {
  return Float64Array.from({ length: z.length }, (_, i) => (z[i] >= a - 1e-9 && z[i] <= b + 1e-9 ? v[i] : NaN));
}

/** A série da sugestão como o motor a usa: o Ic, ou log₁₀ qt (no T-ratio sobre qt e no agrupamento). */
export function serieDaSugestao(r: Resultado, s: SugestaoDeCamadas): Float64Array {
  const n = r.col.z.length;
  return grandezaDaTrilha(s).chave === 'qt'
    ? Float64Array.from({ length: n }, (_, i) => log10(r.col.qt ? r.col.qt[i] : NaN))
    : Float64Array.from({ length: n }, (_, i) => (r.col.ic ? r.col.ic[i] : NaN));
}

/**
 * A média da grandeza em cada camada sugerida, nas leituras de [topo, base) (a última leitura na camada que acaba nela): o
 * Ic médio, ou — sobre log₁₀ qt — 10 elevado à média de log₁₀ qt, em MPa. NaN na camada sem leitura com valor.
 */
export function mediasDasCamadas(r: Resultado, s: SugestaoDeCamadas): number[] {
  const z = r.col.z;
  const n = z.length;
  const serie = serieDaSugestao(r, s);
  let zFim = -Infinity;
  for (let i = 0; i < n; i++) if (z[i] > zFim) zFim = z[i];
  const sobreQt = grandezaDaTrilha(s).chave === 'qt';
  return s.camadas.map((c) => {
    let soma = 0;
    let k = 0;
    const ultima = c.base >= zFim - 1e-9;
    for (let i = 0; i < n; i++) {
      if (z[i] >= c.topo - 1e-9 && (z[i] < c.base - 1e-9 || (ultima && z[i] <= c.base + 1e-9)) && Number.isFinite(serie[i])) {
        soma += serie[i];
        k++;
      }
    }
    const m = k > 0 ? soma / k : NaN;
    return sobreQt ? 10 ** m : m;
  });
}

/** O traço de uma série: os pontos com número, quebrado onde falta (sem NaN no `d`). */
function traco(z: ArrayLike<number>, v: ArrayLike<number>, x: (v: number) => number, y: (z: number) => number,
  a: number, b: number): string {
  let d = '';
  let caneta = false;
  for (let i = 0; i < z.length; i++) {
    const px = x(v[i]);
    const py = y(z[i]);
    if (!(z[i] >= a - 1e-9 && z[i] <= b + 1e-9) || !Number.isFinite(px) || !Number.isFinite(py)) { caneta = false; continue; }
    d += `${caneta ? 'L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)}`;
    caneta = true;
  }
  return d;
}

/** O número de uma marca do eixo x, com as casas que separam as vizinhas. */
function rotulo(v: number, todas: readonly number[]): string {
  const passo = todas.length > 1 ? Math.abs(todas[1] - todas[0]) : Math.abs(v);
  const casas = passo > 0 && Number.isFinite(passo) ? Math.max(0, Math.min(3, -Math.floor(Math.log10(passo)))) : 0;
  return numeroBr(v, casas);
}

export function TrilhasDaSugestao(p: {
  r: Resultado;
  /** A sugestão desenhada (no agrupamento, com os custos). */
  sugestao: SugestaoDeCamadas & { custos?: readonly { grupos: number; custo: number }[] };
  /** O nome de cada camada (a unidade da tabela); sem ele, C1…Cn. */
  unidades?: readonly string[];
  /** O zoom de profundidade (R13): o trecho em m; null, a vertical inteira. */
  zoom: TrechoDoZoom | null;
  aoZoom(z: TrechoDoZoom | null): void;
  /** A escala horizontal por chave (R12); com `aoMudarEscala`, o botão «Escala» nas trilhas da grandeza e do T. */
  escalas?: Readonly<Record<string, EscalaDoGrafico>>;
  aoMudarEscala?(grandeza: string, e: EscalaDoGrafico | null): void;
}): JSX.Element {
  const { r, sugestao } = p;
  const [ref, largura] = useLargura<HTMLDivElement>();
  const z = r.col.z;
  const n = z.length;
  const zFim = useMemo(() => {
    let m = 0;
    for (let i = 0; i < n; i++) if (z[i] > m) m = z[i];
    return m;
  }, [z, n]);
  const zoom = zoomRecortado(p.zoom, [0, zFim]);
  const janela: [number, number] = zoom ? [zoom.de, zoom.ate] : [0, Math.max(1, Math.ceil(zFim))];
  const ez = escala('linear', janela, [CABECALHO, CABECALHO + ALTURA]);
  const naJanela = (x: number): boolean => x >= janela[0] - 1e-9 && x <= janela[1] + 1e-9;

  const g = grandezaDaTrilha(sugestao);
  // A série da grandeza, como o motor a usa (o Ic, ou log₁₀ qt), e o que se desenha (o Ic, ou o qt em MPa).
  const serie = useMemo(() => serieDaSugestao(r, sugestao), [r, sugestao]);
  const desenhada = useMemo(() => (g.chave === 'qt'
    ? Float64Array.from({ length: n }, (_, i) => (r.col.qt ? r.col.qt[i] : NaN))
    : serie), [r, n, g.chave, serie]);
  // O T de cada leitura, pela mesma conta do motor (só no T-ratio).
  const t = useMemo(() => (sugestao.metodo === 'tratio' && Number.isFinite(sugestao.parametros.janela)
    ? tRatio(z, serie, sugestao.parametros.janela) : null), [sugestao, z, serie]);
  // A média de cada camada (na série do motor), para os degraus.
  const medias = useMemo(() => mediasDasCamadas(r, sugestao), [r, sugestao]);

  const util = Math.max(4 * LARGURA_MINIMA, largura - EIXO);
  const lt = util / 4;
  const x0 = (k: number): number => EIXO + k * lt;

  // A trilha do T: o automático do trecho visível, com o limite dentro.
  const limite = sugestao.parametros.limite;
  const ajusteT = p.escalas?.tratio ?? null;
  const tipoT = ajusteT?.tipo ?? 'linear';
  const valoresT = t ? [zoom ? noTrecho(z, t, janela[0], janela[1]) : t, ...(Number.isFinite(limite) ? [[limite]] : [])] : [];
  const dominioT = ajusteT && (limitesFixos(ajusteT) || tipoT === 'log')
    ? dominioDaEscala(ajusteT, dominioDe(valoresT, tipoT, { comZero: tipoT === 'linear' }))
    : dominioDe(valoresT, 'linear', { comZero: true });
  const exT = escala(tipoT, dominioT, [x0(0) + 6, x0(1) - 6]);
  const marcasT = ajusteT ? marcasDaEscala(ajusteT, dominioT, 3) : marcas('linear', dominioT, 3);

  // A trilha da grandeza: no qt, o log de partida não existe (as escalas de hoje são lineares); a escolhida vale.
  const ajusteG = p.escalas?.[g.chave] ?? null;
  const tipoG = ajusteG?.tipo ?? 'linear';
  const valoresG = [zoom ? noTrecho(z, desenhada, janela[0], janela[1]) : desenhada, medias];
  const hojeG: [number, number] = g.chave === 'ic' && tipoG === 'linear' && !limitesFixos(ajusteG) ? [1, 4]
    : dominioDe(valoresG, tipoG, { comZero: tipoG === 'linear' });
  const dominioG = ajusteG && (limitesFixos(ajusteG) || tipoG === 'log') ? dominioDaEscala(ajusteG, hojeG) : hojeG;
  const exG = escala(tipoG, dominioG, [x0(1) + 6, x0(2) - 6]);
  const marcasG = ajusteG ? marcasDaEscala(ajusteG, dominioG, 3) : marcas(tipoG, dominioG, 3);

  // A zona SBTn medida: um retângulo por trecho de leituras seguidas da mesma zona.
  const zonas = useMemo(() => {
    const saida: { de: number; ate: number; zona: number }[] = [];
    const sbtn = r.sbtn;
    if (!sbtn) return saida;
    for (let i = 0; i < n; i++) {
      const zona = sbtn[i];
      if (!(zona >= 1 && zona <= 9)) continue;
      const de = i === 0 ? z[i] : (z[i - 1] + z[i]) / 2;
      const ate = i + 1 < n ? (z[i] + z[i + 1]) / 2 : z[i];
      const ultimo = saida[saida.length - 1];
      if (ultimo && ultimo.zona === zona && Math.abs(ultimo.ate - de) < 1e-6) ultimo.ate = ate;
      else saida.push({ de, ate, zona });
    }
    return saida;
  }, [r, z, n]);
  const zonasPresentes = [...new Set(zonas.map((x) => x.zona))].sort((a, b) => a - b);

  // O gesto do zoom (o da Tarefa 4): para baixo, o trecho; para cima, a vertical inteira; o clique não faz nada.
  const [previa, setPrevia] = useState<{ de: number; ate: number } | null>(null);
  const zDoPonteiro = (e: PointerEvent<SVGRectElement>): number => {
    const caixa = (e.currentTarget.ownerSVGElement ?? e.currentTarget).getBoundingClientRect();
    return Math.min(janela[1], Math.max(janela[0], ez.de(e.clientY - caixa.top)));
  };
  const gesto = useArrastoVertical<SVGRectElement>({
    caixa: ref,
    valor: zDoPonteiro,
    minimo: ARRASTO_DO_ZOOM,
    toqueLongo: true,
    prenderAoApertar: true,
    aoPrevia: (a) => setPrevia(a && a.desce && a.fim !== a.inicio ? { de: Math.min(a.inicio, a.fim), ate: Math.max(a.inicio, a.fim) } : null),
    aoSoltar: (a) => {
      if (!a.desce) { p.aoZoom(null); return; }
      const trecho = zoomRecortado({ de: Math.min(a.inicio, a.fim), ate: Math.max(a.inicio, a.fim) }, [0, zFim]);
      if (trecho) p.aoZoom(trecho);
    },
  });

  const larguraTotal = EIXO + 4 * lt;
  const alturaTotal = CABECALHO + ALTURA + RODAPE;
  const zMarcas = marcas('linear', janela, 6);
  const titulos = [
    sugestao.metodo === 'tratio' ? 'T-ratio' : 'Custo × grupos',
    g.unidade ? `${g.titulo} (${g.unidade})` : g.titulo,
    'Zona SBTn',
    'Camadas',
  ];
  const nomes = sugestao.camadas.map((_, k) => p.unidades?.[k] ?? `C${k + 1}`);

  // O agrupamento: o custo W(k) pelo número de grupos, de 1 ao máximo, com o escolhido.
  const custos = sugestao.metodo === 'agrupamento' ? (sugestao.custos ?? []).filter((c) => Number.isFinite(c.custo)) : [];
  const escolhido = Number.isFinite(sugestao.parametros.grupos) ? sugestao.parametros.grupos : sugestao.camadas.length;
  const gruposX = custos.length > 0 ? escala('linear', [1, Math.max(2, ...custos.map((c) => c.grupos))], [x0(0) + 10, x0(1) - 10]) : null;
  const custoMax = custos.reduce((m, c) => Math.max(m, c.custo), 0) || 1;
  const custoY = escala('linear', [0, custoMax], [CABECALHO + ALTURA - MARGEM_DO_CUSTO, CABECALHO + MARGEM_DO_CUSTO]);

  return (
    <div ref={ref} className="cptu-trilhas-da-sugestao" data-de={janela[0]} data-ate={janela[1]}>
      <BarraDoZoom zoom={zoom} limites={[0, zFim]} rotulo="Profundidade" aoZoom={p.aoZoom} />
      <div className="cptu-linha-de-trilhas">
        <svg className="cptu-trilhas-linha" width={larguraTotal} height={alturaTotal} role="img"
          aria-label={`As trilhas da sugestão: ${titulos.join(', ')}, com as fronteiras sugeridas tracejadas`}>
          <text className="cptu-texto cptu-texto-2" x={EIXO - 6} y={CABECALHO - 8} textAnchor="end">z (m)</text>
          {zMarcas.map((zz) => (
            <g key={`z${zz}`}>
              <line className="cptu-grade" x1={EIXO} x2={larguraTotal} y1={ez.para(zz)} y2={ez.para(zz)} />
              <text className="cptu-texto cptu-texto-2" x={EIXO - 6} y={ez.para(zz) + 4} textAnchor="end">{rotulo(zz, zMarcas)}</text>
            </g>
          ))}

          {titulos.map((titulo, k) => (
            <g key={titulo} className="cptu-trilha-da-sugestao"
              data-trilha={['estatistica', 'grandeza', 'zona', 'camadas'][k]}>
              <rect className="cptu-moldura" x={x0(k) + 2} y={CABECALHO} width={lt - 4} height={ALTURA} />
              <text className="cptu-texto cptu-titulo" x={x0(k) + 4} y={14}>{titulo}</text>
            </g>
          ))}

          {/* 1 · a estatística: o T com o limite e os picos, ou o custo pelo número de grupos. */}
          {t ? (
            <g className="cptu-estatistica-da-sugestao" data-limite={limite}>
              {marcasT.map((v) => (
                <g key={`t${v}`}>
                  <line className="cptu-grade" x1={exT.para(v)} x2={exT.para(v)} y1={CABECALHO} y2={CABECALHO + ALTURA} />
                  <text className="cptu-texto cptu-texto-2" x={exT.para(v)} y={CABECALHO - 6} textAnchor="middle">{rotulo(v, marcasT)}</text>
                </g>
              ))}
              <path className="cptu-serie" d={traco(z, t, (v) => exT.para(v), (zz) => ez.para(zz), janela[0], janela[1])} />
              {Number.isFinite(exT.para(limite)) ? (
                <line className="cptu-sugestao cptu-limite-do-t" x1={exT.para(limite)} x2={exT.para(limite)} y1={CABECALHO} y2={CABECALHO + ALTURA} />
              ) : null}
              {sugestao.fronteiras.filter(naJanela).map((f) => {
                const i = z.findIndex((x) => Math.abs(x - f) < 1e-6);
                const x = i >= 0 ? exT.para(t[i]) : NaN;
                return Number.isFinite(x) ? <circle key={`p${f}`} className="cptu-pico-do-t" cx={x} cy={ez.para(f)} r={3} /> : null;
              }).filter(Boolean)}
            </g>
          ) : null}
          {gruposX ? (
            <g className="cptu-estatistica-da-sugestao" data-escolhido={escolhido}>
              <path className="cptu-serie" d={custos.map((c, k) => `${k === 0 ? 'M' : 'L'}${gruposX.para(c.grupos).toFixed(1)} ${custoY.para(c.custo).toFixed(1)}`).join('')} />
              {custos.map((c) => (
                <circle key={`k${c.grupos}`} className={c.grupos === escolhido ? 'cptu-pico-do-t' : 'cptu-ponto-do-custo'}
                  cx={gruposX.para(c.grupos)} cy={custoY.para(c.custo)} r={c.grupos === escolhido ? 4 : 2} />
              ))}
              {custos.filter((c) => c.grupos === escolhido).map((c) => (
                <text key="escolhido" className="cptu-texto cptu-texto-2" x={gruposX.para(c.grupos)} y={custoY.para(c.custo) - 8} textAnchor="middle">
                  {`${c.grupos} grupos`}
                </text>
              ))}
              <text className="cptu-texto cptu-texto-2" x={(x0(0) + x0(1)) / 2} y={CABECALHO + ALTURA - 6} textAnchor="middle">grupos</text>
            </g>
          ) : null}

          {/* 2 · a grandeza, com a média de cada camada em degraus. */}
          <g className="cptu-grandeza-da-sugestao" data-grandeza={g.chave}>
            {marcasG.map((v) => (
              <g key={`g${v}`}>
                <line className="cptu-grade" x1={exG.para(v)} x2={exG.para(v)} y1={CABECALHO} y2={CABECALHO + ALTURA} />
                <text className="cptu-texto cptu-texto-2" x={exG.para(v)} y={CABECALHO - 6} textAnchor="middle">{rotulo(v, marcasG)}</text>
              </g>
            ))}
            <path className={`cptu-serie serie-${g.chave}`} d={traco(z, desenhada, (v) => exG.para(v), (zz) => ez.para(zz), janela[0], janela[1])} />
            {sugestao.camadas.map((c, k) => {
              const x = exG.para(medias[k]);
              const a = Math.max(janela[0], c.topo);
              const b = Math.min(janela[1], c.base);
              return Number.isFinite(x) && b > a
                ? <line key={`m${c.topo}`} className="cptu-media-da-camada" data-media={medias[k]} x1={x} x2={x} y1={ez.para(a)} y2={ez.para(b)} />
                : null;
            })}
          </g>

          {/* 3 · a zona SBTn medida, leitura a leitura. */}
          <g className="cptu-zona-da-sugestao">
            {zonas.filter((x) => x.ate >= janela[0] && x.de <= janela[1]).map((x) => {
              const a = ez.para(Math.max(janela[0], x.de));
              const b = ez.para(Math.min(janela[1], x.ate));
              const zona = ZONAS_SBTN[x.zona as keyof typeof ZONAS_SBTN];
              return (
                <rect key={`s${x.de}`} x={x0(2) + 4} y={a} width={lt - 8} height={Math.max(0.5, b - a)} fill={`var(${zona.token})`}>
                  <title>{`SBTn ${x.zona} · ${zona.nome}`}</title>
                </rect>
              );
            })}
          </g>

          {/* 4 · as camadas sugeridas, com o nome. */}
          <g className="cptu-camadas-da-sugestao">
            {sugestao.camadas.map((c, k) => {
              const a = Math.max(janela[0], c.topo);
              const b = Math.min(janela[1], c.base);
              if (!(b > a)) return null;
              const ya = ez.para(a);
              const yb = ez.para(b);
              return (
                <g key={`c${c.topo}`} data-camada={k + 1}>
                  <rect className="cptu-unidade" x={x0(3) + 4} y={ya} width={lt - 8} height={Math.max(0.5, yb - ya)} />
                  {yb - ya >= 14 ? (
                    <text className="cptu-texto cptu-nome-da-unidade" x={x0(3) + 8} y={(ya + yb) / 2 + 4}>{`C${k + 1}`}</text>
                  ) : null}
                  <title>{`C${k + 1} · ${nomes[k]}: ${numeroBr(c.topo, 2)} – ${numeroBr(c.base, 2)} m`}</title>
                </g>
              );
            })}
          </g>

          {/* As fronteiras sugeridas, tracejadas, atravessando as quatro trilhas. */}
          {sugestao.fronteiras.filter(naJanela).map((f) => (
            <line key={`f${f}`} className="cptu-sugestao" data-z={f} x1={EIXO} x2={larguraTotal} y1={ez.para(f)} y2={ez.para(f)} />
          ))}

          {previa ? (
            <g>
              <rect className="cptu-selecao cptu-previa-do-zoom" x={EIXO} width={4 * lt}
                y={ez.para(previa.de)} height={Math.max(1, ez.para(previa.ate) - ez.para(previa.de))} />
              <text className="cptu-texto cptu-previa-do-zoom-texto" x={EIXO + 4} y={ez.para(previa.de) - 4}>
                {`${numeroBr(previa.de, 2)} → ${numeroBr(Math.min(previa.ate, zFim), 2)} m`}
              </text>
            </g>
          ) : null}

          <rect className="cptu-captura" x={EIXO} y={CABECALHO} width={4 * lt} height={ALTURA}
            onPointerDown={gesto.onPointerDown} onPointerMove={gesto.onPointerMove} onPointerUp={gesto.onPointerUp}
            onPointerCancel={() => gesto.onPointerCancel()} />
        </svg>
        {p.aoMudarEscala ? (
          <>
            {t ? (
              <div className="cptu-escala-na-trilha" style={{ left: x0(1) - 4, top: 0 }}>
                <ControleDaEscala grandeza="tratio" rotulo="T" unidade="" escala={ajusteT} dominio={dominioT}
                  aoMudar={(e) => p.aoMudarEscala?.('tratio', e)} />
              </div>
            ) : null}
            <div className="cptu-escala-na-trilha" style={{ left: x0(2) - 4, top: 0 }}>
              <ControleDaEscala grandeza={g.chave} rotulo={g.titulo} unidade={g.unidade} escala={ajusteG} dominio={dominioG}
                aoMudar={(e) => p.aoMudarEscala?.(g.chave, e)} />
            </div>
          </>
        ) : null}
      </div>
      {/* A cor nunca sozinha: as zonas que aparecem, com o número e o nome; e o que cada traço é. */}
      <p className="cptu-nota cptu-legenda-da-sugestao">
        {sugestao.metodo === 'tratio'
          ? `T-ratio sobre ${g.chave === 'qt' ? 'log qt' : 'o Ic'}: o limite (${numeroBr(limite, 2)}) tracejado; os picos que viraram fronteira, marcados. `
          : `O custo do agrupamento pelo número de grupos; o escolhido (${escolhido}), marcado. `}
        {g.chave === 'qt' ? 'Na trilha do qt, a média de log qt de cada camada (em MPa). ' : 'Na trilha do Ic, a média de cada camada. '}
        {zonasPresentes.length > 0
          ? `Zonas SBTn: ${zonasPresentes.map((k) => `${k} · ${ZONAS_SBTN[k as keyof typeof ZONAS_SBTN].nome}`).join('; ')}.`
          : 'Sem zona SBTn nas leituras.'}
      </p>
    </div>
  );
}
